import { Box, styled } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import type { Control, FieldValues } from 'react-hook-form';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import { useAppSelector } from '@/hooks/useAppSelector';
import * as reservationSlice from '@/data/reservation.slice';
import * as lookupSlice from '@/data/lookup.slice';
import {
  type TripArrayFieldKey,
  useReservationFormMachines,
} from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../useFieldActorSnapshot';
import reservationUtils from '@/services/reservationUtils';

function mergeDatePart(
  base: DateTime | null,
  newDate: DateTime | null,
): DateTime | null {
  if (newDate === null) {
    return null;
  }
  if (!newDate.isValid) {
    return base;
  }
  if (!base?.isValid) {
    return null;
  }
  return base?.set({
    year: newDate.year,
    month: newDate.month,
    day: newDate.day,
  });
}

function mergeTimePart(
  base: DateTime | null,
  newTime: DateTime | null,
): DateTime | null {
  if (newTime === null) {
    return null;
  }
  if (!newTime.isValid) {
    return base;
  }
  if (!base?.isValid) {
    return newTime;
  }
  return base.set({
    hour: newTime.hour,
    minute: newTime.minute,
    second: newTime.second,
  });
}

const PREFIX = 'TripArrayFieldDateTimeInput';

const classes = {
  container: `${PREFIX}-container`,
  appointment: `${PREFIX}-appointment`,
  pickup: `${PREFIX}-pickup`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    '& > *': { flex: '1 1 140px', minWidth: 0 },
    [`&.${classes.pickup}`]: {
      marginLeft: theme.spacing(4),
      marginTop: theme.spacing(-1),
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: '75%',
    },
  },
}));

export function TripArrayFieldDateTimeInput({
  tripField,
  index,
  required,
  disabled,
  readOnly,
}: {
  /** Luxon datetime; `appointmentDate` / `pickupDate` are separate string fields synced from this control. */
  tripField: Extract<
    TripArrayFieldKey,
    'appointmentDateTime' | 'pickupDateTime'
  >;
  index: number;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
}) {
  const { control, setValue } = useFormContext<ReservationFormInput>();
  const { t } = useTranslation('common');
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs[tripField];
  useFieldActorSnapshot(actor);

  const timeZones = useAppSelector(lookupSlice.selectTimeZones);

  const pickupTimeZone = useWatch({
    control,
    name: `trips.${index}.pickupTimeZone`,
  }) as string | null | undefined;

  const pickupTimeZoneName = reservationUtils.getTimeZoneAbbrev(pickupTimeZone ?? '', timeZones);

  const dateFieldLabel =
    tripField === 'appointmentDateTime'
      ? t('member.apptDate')
      : t('member.date');

  const timeFieldLabel =
    tripField === 'pickupDateTime' && pickupTimeZoneName
      ? `${t('member.time')} (${pickupTimeZoneName})`
      : t('member.time');

  const requiredFieldMessage = t('common.requiredField');
  const invalidDateMessage = t('common.invalidDate');
  const name = `trips.${index}.${tripField}` as const;
  const dateField =
    tripField === 'pickupDateTime' ? 'pickupDate' : 'appointmentDate';
  const dateFieldName = `trips.${index}.${dateField}` as const;
  const pairedDateIso = useWatch({
    control,
    name: dateFieldName,
  }) as string | undefined;
  const pairedDateValue = pairedDateIso
    ? DateTime.fromISO(pairedDateIso)
    : null;
  const estimatedTripTime = useAppSelector(reservationSlice.selectEstimatedTripTime);
  const appointmentDateTime = useWatch({
    control,
    name: `trips.${index}.appointmentDateTime`,
  }) as DateTime | null | undefined;
  const pickupOffsetMinutes = estimatedTripTime?.totalPickupTime ?? 0;

  const selectedDate = (fieldValue: DateTime | null | undefined) =>
    fieldValue ?? pairedDateValue ?? null;

  const sendChange = (next: DateTime | null) => {
    actor.send({
      type: 'CHANGE',
      index,
      value: next?.toISO() ?? '',
    });
  };

  const syncPairedDateString = (next: DateTime | null) => {
    setValue(dateFieldName, next?.toISODate() ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <Controller
      name={name}
      control={control as unknown as Control<FieldValues>}
      rules={{
        required: required && requiredFieldMessage,
        validate: (value: DateTime | null) => {
          if (required && !value?.isValid) {
            return invalidDateMessage;
          }
          if (!required && value && !value.isValid) {
            return invalidDateMessage;
          }
          if (
            tripField === 'pickupDateTime' &&
            value?.isValid &&
            appointmentDateTime?.isValid
          ) {
            const minutesUntilAppointment = appointmentDateTime.diff(
              value,
              'minutes',
            ).minutes;
            if (minutesUntilAppointment < pickupOffsetMinutes) {
              return t('reservation.pickupTimeOffsetError', {
                minutes: pickupOffsetMinutes,
              });
            }
          }
          return true;
        },
      }}
      render={({ field, fieldState }) => {
        const effectiveSelectedDate = selectedDate(field.value);
        const pickupMaxDate =
          tripField === 'pickupDateTime' && appointmentDateTime?.isValid
            ? appointmentDateTime
            : undefined;
        const pickupMaxTime =
          tripField === 'pickupDateTime' &&
          appointmentDateTime?.isValid &&
          effectiveSelectedDate?.isValid &&
          effectiveSelectedDate.hasSame(appointmentDateTime, 'day')
            ? appointmentDateTime.minus({ minutes: pickupOffsetMinutes })
            : undefined;

        const helperText = fieldState.error?.message;

        return (
          <StyledBox
            className={`${classes.container} ${tripField === 'appointmentDateTime' ? classes.appointment : classes.pickup}`}>
            <DatePicker
              label={dateFieldLabel}
              value={field.value ?? pairedDateValue ?? null}
              disabled={disabled}
              readOnly={readOnly}
              minDate={tripField === 'pickupDateTime' ? undefined : undefined}
              maxDate={pickupMaxDate}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  required,
                  error: !!fieldState.error,
                  helperText: tripField === 'appointmentDateTime' ? null : helperText,
                  onFocus: () => actor.send({ type: 'FOCUS', index }),
                  sx: {
                    width: '100%',
                    '& .MuiInputAdornment-root .MuiIconButton-root': {
                      padding: '4px',
                      marginLeft: '-15px',
                    },
                  },
                },
              }}
              onChange={(date) => {
                const next = mergeDatePart(field.value ?? null, date);
                const hasExistingDateTime =
                  field.value instanceof DateTime && field.value.isValid;

                if (hasExistingDateTime) {
                  field.onChange(next);
                  syncPairedDateString(next);
                  sendChange(next);
                  return;
                }

                // Keep date-only selection without fabricating a midnight time.
                field.onChange(null);
                setValue(dateFieldName, date?.toISODate() ?? '', {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                sendChange(null);
              }}
            />
            <TimePicker
              label={timeFieldLabel}
              value={field.value ?? null}
              disabled={disabled}
              readOnly={readOnly}
              maxTime={pickupMaxTime}
              closeOnSelect={false}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  required,
                  error: !!fieldState.error,
                  helperText: tripField === 'appointmentDateTime' ? null : helperText,
                  onFocus: () => actor.send({ type: 'FOCUS', index }),
                  sx: { width: '100%' },
                },
              }}
              onChange={(time) => {
                const fallbackBase =
                  pairedDateValue && pairedDateValue.isValid
                    ? pairedDateValue.startOf('day')
                    : null;
                const next = mergeTimePart(
                  (field.value ?? fallbackBase) as DateTime | null,
                  time,
                );
                field.onChange(next);
                syncPairedDateString(next ?? fallbackBase);
                sendChange(next);
                if (time) {
                  field.onBlur();
                }
              }}
            />
          </StyledBox>
        );
      }}
    />
  );
}
